<template>
  <div
    class="base-file-uploader"
    :class="computedClasses"
    @dragover.prevent="onDragOver"
    @dragleave="onDragLeave"
    @drop.prevent="onDrop"
  >
    <div
      class="base-file-uploader__wrapper"
      @click="triggerFileInput"
    >
      <input
        ref="fileInputRef"
        type="file"
        class="base-file-uploader__input"
        :multiple="multiple"
        :accept="accept"
        :readonly="props.readonly"
        @change="onFileSelect"
      >
      <div class="base-file-uploader__area">
        <slot name="area">
          <BaseIcon
            v-if="props.withIcon"
            name="vuesax:gallery-export"
            size="50px"
            color="var(--color-foreground-primary)"
          />
          <slot name="caption">
            <BaseTypography
              :type="props.size === 'mini' ? 'text-secondary' : 'text'"
              :color="props.size === 'mini' ? 'var(--color-foreground-caption)' : 'var(--color-foreground-regular)'"
            >
              Перетащите файл / изображение в эту область
            </BaseTypography>
            <br v-if="props.caption">
            <BaseTypography
              v-if="props.caption"
              type="text-secondary"
              color="var(--color-foreground-caption)"
            >
              {{ props.caption }}
            </BaseTypography>
          </slot>
        </slot>
      </div>
      <ul v-if="props.withPreview" class="base-file-uploader__preview">
        <li
          v-for="(item, index) in internalFiles"
          :key="index"
          class="base-file-uploader__item"
          @click.stop="removeFile(index)"
        >
          <span class="base-file-uploader__name">{{ item.name }}</span>
          <BaseIcon
            name="vuesax:close-linear"
            size="14px"
            color="var(--color-foreground-light)"
          />
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { MAX_MEDIA_VIDEO_SIZE, MB } from '../../const/files'
import { useNotificationStore } from '../../lib/use/notification'
import BaseIcon from '../icon/index.vue'
import BaseTypography from '../typography/index.vue'

interface InternalFile {
  file: File
  name: string
  preview?: string
}

interface Props {
  multiple?: boolean
  accept?: string
  maxSize?: number // in bytes (ex: 5MB = 5 * 1024 * 1024)
  maxFiles?: number
  caption?: string
  withIcon?: boolean
  height?: string
  size?: 'mini' | 'md'
  readonly?: boolean
  withPreview?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  withIcon: true,
  height: '216px',
  size: 'md',
  withPreview: true,
  maxSize: MAX_MEDIA_VIDEO_SIZE,
})

const emit = defineEmits<{
  (e: 'uploadedFirstFile', file: File): void
}>()

const modelValue = defineModel<File[]>({ required: true })
const { notify } = useNotificationStore()
const fileInputRef = ref<HTMLInputElement>()
const isDragging = ref(false)

const internalFiles = ref<InternalFile[]>([])

const computedClasses = computed(() => ({
  'base-file-uploader--dragging': isDragging.value,
  [`base-file-uploader--size-${props.size}`]: props.size !== 'md',
}))

const onDragOver = () => (isDragging.value = true)
const onDragLeave = () => (isDragging.value = false)
function triggerFileInput() {
  if (props.readonly) {
    return
  }

  fileInputRef.value?.click()
}

function onDrop(event: DragEvent) {
  isDragging.value = false
  handleFiles(event.dataTransfer?.files)
}

function onFileSelect(event: Event) {
  if (props.readonly) {
    return
  }

  const input = event.target as HTMLInputElement
  if (input.files) {
    handleFiles(input.files)
    input.value = ''
  }
}

function handleFiles(fileList?: FileList | null) {
  if (!fileList) {
    return
  }

  const newFiles: InternalFile[] = []

  const sourceFiles = props.multiple
    ? Array.from(fileList)
    : Array.from(fileList).slice(0, 1)

  sourceFiles.forEach((file) => {
    if (props.maxSize && file.size > props.maxSize) {
      notify({
        title: 'Файл слишком большой',
        message: `Файл ${file.name} превышает максимальный размер в ${props.maxSize / MB} МБ`,
        type: 'warning',
      })
      return
    }

    const fileData = {
      file,
      name: file.name,
      preview: isImage(file) ? URL.createObjectURL(file) : undefined,
    }

    newFiles.push(fileData)
  })

  let filesToAdd = newFiles

  if (props.multiple && props.maxFiles) {
    const toAdd = props.maxFiles - internalFiles.value.length
    if (toAdd <= 0) {
      notify({
        title: 'Достигнут лимит',
        message: `Максимум ${props.maxFiles} файлов`,
        type: 'warning',
      })
      return
    }
    if (newFiles.length > toAdd) {
      filesToAdd = newFiles.slice(0, toAdd)
      notify({
        title: 'Достигнут лимит',
        message: `Добавлено ${toAdd} из ${newFiles.length} файлов. Максимум ${props.maxFiles} файлов`,
        type: 'warning',
      })
    }
  }

  internalFiles.value = props.multiple
    ? [...internalFiles.value, ...filesToAdd]
    : filesToAdd
  modelValue.value = internalFiles.value.map(f => f.file)

  const firstFile = internalFiles.value?.[0]?.file

  if (firstFile) {
    emit('uploadedFirstFile', firstFile)
  }
}

function isImage(file: File) {
  return file?.type?.startsWith('image/') ?? false
}

function removeFile(index: number) {
  internalFiles.value.splice(index, 1)
  modelValue.value = internalFiles.value.map(f => f.file)
}

watch(
  modelValue,
  (newFiles) => {
    if (!newFiles) {
      return
    }

    internalFiles.value = newFiles.map(file => ({
      file,
      name: file.name,
      preview: isImage(file) ? URL.createObjectURL(file) : undefined,
    }))
  },
  { immediate: true },
)
</script>

<style lang="scss" scoped>
.base-file-uploader {
  position: relative;
  transition: all 0.15s ease-in-out;

  &__wrapper {
    height: var(--base-file-uploader-height, v-bind(height));
    display: flex;
    align-items: center;
    justify-content: center;
    background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='20' ry='20' stroke='%23A4A4A4FF' stroke-width='3' stroke-dasharray='16%2c 24' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
    border-radius: var(--radius-sm);
    padding: 20px;
    text-align: center;
    cursor: pointer;
    transition: 0.3s;
  }

  &__area {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;

    span {
      max-width: 464px;
    }
  }

  &__input {
    display: none;
  }

  &__preview {
    position: absolute;
    bottom: rem(10px);
    left: rem(10px);
    width: calc(100% - rem(10px) - rem(10px));
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    gap: rem(10px);
    overflow-x: auto;
  }

  &__item {
    @include action-effect-button(var(--color-surface-primary));

    display: flex;
    align-items: center;
    gap: rem(5px);
    border-radius: var(--radius-md);
    color: var(--color-foreground-light);
    padding: rem(5px) rem(10px);
  }

  &__image {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: 4px;
  }

  &__name {
    @include typo-caption;

    white-space: nowrap;
  }

  &__remove {
    background: none;
    border: none;
    color: red;
    font-size: 16px;
    cursor: pointer;
  }

  &--dragging {
    background-color: rgb(26 92 255 / 5%);
    scale: 1.05;
  }

  &--size {
    &-mini {
      .base-file-uploader__wrapper {
        height: var(--base-file-uploader-height, 70px);
      }
    }
  }
}
</style>
